import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { TableColumn } from '../../../types/admin.types';

export interface DataTableProps<T> {
  columns: TableColumn[];
  rows: T[];
  loading?: boolean;
  page?: number;
  rowsPerPage?: number;
  total?: number;
  onPageChange?: (event: unknown, newPage: number) => void;
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  onView?: (row: T) => void;
  getRowId?: (row: T) => string | number;
  showActions?: boolean;
}

export const DataTable = React.forwardRef<HTMLDivElement, DataTableProps<any>>(
  (
    {
      columns,
      rows,
      loading = false,
      page = 0,
      rowsPerPage = 10,
      total = 0,
      onPageChange,
      onRowsPerPageChange,
      onEdit,
      onDelete,
      onView,
      getRowId = (row) => row._id || row.id,
      showActions = true,
    },
    ref
  ) => {
    const actionColumn: TableColumn = {
      id: 'actions',
      label: 'Actions',
      minWidth: 150,
      align: 'center',
    };

    const displayColumns = showActions ? [...columns, actionColumn] : columns;

    return (
      <TableContainer
        component={Paper}
        ref={ref}
        sx={{
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          border: '1px solid #E6E7EB',
        }}
      >
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 300,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {!loading && rows.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 300,
            }}
          >
            <Typography color="textSecondary">No data available</Typography>
          </Box>
        )}

        {!loading && rows.length > 0 && (
          <>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F2F3F5' }}>
                  {displayColumns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align || 'left'}
                      style={{ minWidth: column.minWidth || 100, fontWeight: 600, color: '#333333', backgroundColor: '#F2F3F5', borderBottom: '1px solid #E6E7EB' }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={getRowId(row)}
                    hover
                    sx={{
                      '&:hover': {
                        backgroundColor: '#F2F3F5',
                      },
                      borderBottom: '1px solid #E6E7EB',
                    }}
                  >
                    {columns.map((column) => {
                      const value = (row as any)[column.id];
                      return (
                        <TableCell
                          key={`${getRowId(row)}-${column.id}`}
                          align={column.align || 'left'}
                        >
                          {column.format ? column.format(value) : value}
                        </TableCell>
                      );
                    })}
                    {showActions && (
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          {onView && (
                            <Tooltip title="View">
                              <IconButton
                                size="small"
                                onClick={() => onView(row)}
                                sx={{ color: '#005e5d' }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {onEdit && (
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => onEdit(row)}
                                sx={{ color: '#005e5d' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {onDelete && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => onDelete(row)}
                                sx={{ color: '#D32F2F', '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' } }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {total > rowsPerPage && onPageChange && onRowsPerPageChange && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
              />
            )}
          </>
        )}
      </TableContainer>
    );
  }
);

DataTable.displayName = 'DataTable';

export default DataTable;
